import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from "styled-components";
import { ChatComponentType, Commond, Message, ParticipantType } from '@/types';
import { MeetingContext } from "@/pages/Meeting";
import { useMqtt } from "@/hooks/use-mqtt";
import { Button, Modal, Input, Tag, Space, message as antMessage } from 'antd';
import type { InputRef } from 'antd';
import { PlusOutlined, SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { addFilterWord, deleteFilterWord, getAiMinutesList, getKeywords } from './api';
import ee from "@/util/event";

// 样式部分
const Container = styled.div`
  padding: 10px 3px;
  border-bottom: 1px solid rgb(232, 232, 232);
  width: 100%;
  overflow: hidden;
  height: 100%;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px 10px 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const HeaderLeft = styled.div`
  min-width: 0;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #1f2933;
  font-weight: 500;
`;

const HeaderSubTitle = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #7b8794;
`;

const StatusText = styled.span<{ $status: AiRecordStatus }>`
  color: ${props => props.$status === "recording" ? "#1890ff" : props.$status === "completed" ? "#52c41a" : "#8c8c8c"};
  font-weight: 500;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 50px); /* 减去输入框高度 */
  width: 100%;
`;

const ChatWindow = styled.div`
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  background: #f7f8fa;

`;

const MessageRow = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
`;

const MessageUser = styled.div`
  font-size: 12px;
  color: #5f6b7a;
  margin: 0 0 4px 2px;
`;

const MessageContent = styled.div<{ $summary?: boolean }>`
  align-self: flex-start;
  max-width: 92%;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${props => props.$summary ? '#fff7e6' : '#fff'};
  border: 1px solid ${props => props.$summary ? '#ffd591' : '#e8edf3'};
  color: #1f2933;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MessageTimestamp = styled.div`
  color: #aaa;
  font-size: 12px;
  margin: 4px 0 0 2px;
`;

const EmptyState = styled.div`
  height: 100%;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #8c8c8c;
  font-size: 13px;
  padding: 24px;
`;

type AiRecordStatus = "idle" | "recording" | "completed";

interface AiRecordMessage extends Message {
  type?: string;
  sourceId?: string;
  userName?: string;
  startMs?: number;
  createdAt?: string;
}

const normalizeAiRecordMessage = (payload: any): AiRecordMessage | null => {
  if (!payload) return null;
  const data = payload.data ?? payload;
  const content = data.content ?? data.text ?? data.summary ?? "";
  if (!content) return null;
  return {
    id: data.id ?? `${data.type ?? "transcript"}-${data.sourceId ?? data.userId ?? "unknown"}-${data.startMs ?? Date.now()}`,
    type: data.type,
    sourceId: data.sourceId ?? data.userId,
    user: data.userName ?? data.user ?? data.sourceId ?? data.userId,
    userName: data.userName,
    content,
    timestamp: data.timestamp ?? data.createdAt ?? (typeof data.startMs === "number" ? `${Math.round(data.startMs / 1000)}s` : new Date().toLocaleTimeString()),
    startMs: data.startMs,
  };
};

const ChatComponent: React.FC<ChatComponentType> = ({messageList }) =>  {
  const [messages, setMessages] = useState<AiRecordMessage[]>([]);
  const [filterWords, setFilterWords] = useState<any[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [recordStatus, setRecordStatus] = useState<AiRecordStatus>("idle");
  const inputRef = useRef<InputRef>(null);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const { conference: conf, participants } = useContext(MeetingContext);
  const textRecordTopic = `${conf.conferenceNo}_textrecord`;

  const init = () => {
    getKeywords().then((res: any) => {
        setFilterWords(res?.list);
    })

    getAiMinutesList({
      conferenceNo: conf.conferenceNo,
      pageNum: 1,
      pageSize: -1
    }).then((res: any) => {
      setMessages(res?.list ?? []);
    })
  }

  useEffect(() => {
    init();
  }, []);

  const showInput = () => {
    setInputVisible(true);
  };

  useEffect(() => {
    if (inputVisible) {
      inputRef.current?.focus();
    }
  }, [inputVisible]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputConfirm = () => {
    if (inputValue && !filterWords.includes(inputValue)) {
      addFilterWord(inputValue).then(() => {
        antMessage.success('Filter word added successfully');
        init()
      })
    } else if (filterWords.includes(inputValue)) {
      antMessage.warning('Filter word already exists');
    }
    setInputVisible(false);
    setInputValue('');
  };

  const tagPlusStyle: React.CSSProperties = {
    background: '#fff',
    borderStyle: 'dashed',
    cursor: 'pointer'
  };

  const handleRemoveWord = (wordToRemove: string) => {
    deleteFilterWord(wordToRemove).then(init)
  };

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const { setCallback, setTopicCallback, removeTopicCallback } = useMqtt()

  useEffect(() => {
    scrollToBottom();
  }, [messages, messageList]);

  useEffect(() => {
    const statusHandler = (status: AiRecordStatus) => {
      setRecordStatus(status);
    };
    ee.on("aiRecordStatus", statusHandler);
    return () => {
      ee.off("aiRecordStatus", statusHandler);
    };
  }, []);

  const resolveUserName = (msg: AiRecordMessage) => {
    if (msg.type === "summary") return msg.userName || "会议纪要";
    const sourceId = msg.sourceId || msg.user || "";
    const participant = participants.find((item: ParticipantType) => item.peerId === sourceId);
    return participant?.peerName || msg.userName || msg.user || sourceId || "未知用户";
  };

  useEffect(() => {
    const appendRecord = (payload: any) => {
      const next = normalizeAiRecordMessage(payload);
      const data = payload?.data ?? payload;
      if (!next || data?.conferenceNo !== conf.conferenceNo) {
        return;
      }
      setMessages((prev) => {
        if (next.id && prev.some((item) => item.id === next.id)) {
          return prev;
        }
        return [...prev, next];
      });
      if (next.type === "summary") {
        setRecordStatus("completed");
      }
    };
    setCallback(Commond.aiRecord, appendRecord);
    setTopicCallback(textRecordTopic, appendRecord);
    return () => {
      removeTopicCallback(textRecordTopic);
    };
  }, [conf.conferenceNo, textRecordTopic, setCallback, setTopicCallback, removeTopicCallback]);

  const filteredMessages = messages.filter(msg => {
    if (!filterWords?.length) return true;
    return !filterWords?.some(word => msg.content.includes(word.word ?? word));
  });

  const statusLabel = recordStatus === "recording"
    ? "正在会议纪要"
    : recordStatus === "completed"
      ? "会议纪要已完成"
      : "会议纪要未开始";

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <HeaderTitle>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <span>AI纪要</span>
            <StatusText $status={recordStatus}>{statusLabel}</StatusText>
          </HeaderTitle>
          <HeaderSubTitle>自动显示会议转写和最终纪要</HeaderSubTitle>
        </HeaderLeft>
        <Button 
          type="text" 
          icon={<SettingOutlined />} 
          onClick={() => setIsModalVisible(true)}
          size="small"
        >
          过滤词
        </Button>
      </Header>
      <ChatContainer>
        <ChatWindow>
          {filteredMessages.length === 0 ? (
            <EmptyState>
              {recordStatus === "recording" ? "正在等待会议纪要内容..." : "暂无会议纪要内容"}
            </EmptyState>
          ) : (
            filteredMessages.map((message) => (
              <MessageRow key={message.id}>
                <MessageUser>{resolveUserName(message)}:</MessageUser>
                <MessageContent $summary={message.type === "summary"}>{message.content}</MessageContent>
                <MessageTimestamp>{message?.timestamp ?? message?.createdAt}</MessageTimestamp>
              </MessageRow>
            ))
          )}
          <div ref={messageEndRef} />
        </ChatWindow>
      </ChatContainer>

      <Modal
        title="过滤词管理"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            {inputVisible ? (
              <Input
                ref={inputRef}
                type="text"
                size="small"
                style={{ width: 78 }}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputConfirm}
                onPressEnter={handleInputConfirm}
              />
            ) : (
              <Tag onClick={showInput} style={tagPlusStyle}>
                <PlusOutlined /> 添加过滤词
              </Tag>
            )}
          </Space>
        </div>
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {filterWords?.map(item => (
            <Tag
              key={item.id}
              closable
              onClose={() => handleRemoveWord(item.id)}
              style={{ marginBottom: 8 }}
              color="blue"
            >
              {item.word}
            </Tag>
          ))}
          {filterWords?.length === 0 && <span style={{ color: '#999' }}>暂无过滤词</span>}
        </div>
      </Modal>
    </Container>
  );
};

export default ChatComponent;
